<script lang="ts" setup>
  import { onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import WidgetSkeleton from '@/components/dashboard/WidgetSkeleton.vue'
  import type { EmployeeAvatarJsonldDefaultRead, EmployeeJsonldDefaultRead, InfoboardEntryJsonldDefaultRead } from '@/api' // import { useGeneratedWorktimeTrackingEntryStore } from '@/stores'
  import { useGeneratedEmployeeAvatarStore, useGeneratedEmployeeStore, useGeneratedInfoboardEntryStore, useGeneratedInfoboardEntryUserMetaStore } from '@/stores'
  import Image from 'primevue/image'
  import { keyBy } from 'lodash-es'
  import { useTitrop } from '@/composables/useTitrop.ts'
  import { useFormatter } from '@/composables/useFormatter.ts'
  import { RouterLink } from 'vue-router'
  import { useDashboard } from '@/composables/useDashboard.ts'
  import { useInfoboardWidgetBus } from '@/composables/useDashboardWidgetBuses.ts'
  import OverlayBadge from 'primevue/overlaybadge'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import { DateTime } from 'luxon'
  import { Router } from '@/helpers/router.ts'
  import PanelTitled from '@/elements/PanelTitled.vue'
  import { useToast } from 'primevue/usetoast'
  import { ProxyContextMenuPayload } from '@/components/dashboard/GridWidgetProxy.vue'

  interface Props {
    visibleTypes: string[],
    items: number,
    visibleState: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    visibleTypes: () => ['info'],
    items: 20,
    visibleState: false
  })

  const infoboardWidgetBus = useInfoboardWidgetBus()

  const iconsList = {
    info: 'InfoIcon',
    question: 'HelpIcon',
    error: 'BugIcon',
    announce: 'AnnouncementIcon',
    other: 'OtherIcon'
  } as Record<string, string>

  const titrop = useTitrop()
  const toastService = useToast()
  const { t } = useI18n()
  const isLoading = ref(true)
  const entries = ref<InfoboardEntryJsonldDefaultRead[]>([])

  const employeeStore = useGeneratedEmployeeStore()
  const employees = ref<Record<string, EmployeeJsonldDefaultRead>>()

  const isEditing = useDashboard().isEditing
  const isDetailsDialogVisible = ref(false)
  const isLargeScreen = useMediaQuery('(min-width: 768px)')

  const menu = ref()
  const notEditingMenu = ref()
  const preventClose = ref(false)
  let originalHide: (() => void) | null = null
  const items = ref([
    {
      label: t('displayedTypes'),
      icon: 'TypeIcon',
      items: [
        { label: t('information'), value: 'info', icon: 'InfoIcon', selectable: true, behavior: 'toggle-type' },
        { label: t('question'), value: 'question', icon: 'HelpIcon', selectable: true, behavior: 'toggle-type' },
        { label: t('announcement'), value: 'announce', icon: 'AnnouncementIcon', selectable: true, behavior: 'toggle-type' },
        { label: t('error'), value: 'error', icon: 'BugIcon', selectable: true, behavior: 'toggle-type' },
        { label: t('other'), value: 'other', icon: 'OtherIcon', selectable: true, behavior: 'toggle-type' },
        { separator: true },
        { label: t('showAll'), value: false, icon: 'InfoboardOverviewIcon', behavior: 'toggle-read-state' },
        { label: t('unreadOnly'), value: true, icon: 'InfoboardNewItemIcon', behavior: 'toggle-read-state' }
      ]
    },
    {
      label: t('itemCount'),
      icon: 'ItemsAmountIcon',
      items: [
        { label: t('entries', { count: 5 }), value: 5, icon: 'ShortTextIcon', behavior: 'change-items' },
        { label: t('entries', { count: 10 }), value: 10, icon: 'TextIcon', behavior: 'change-items' },
        { label: t('entries', { count: 25 }), value: 25, icon: 'LongTextIcon', behavior: 'change-items' },
        { label: t('all'), value: 99999, icon: 'FullWidgetIcon', behavior: 'change-items' }
      ]
    }
  ])

  const selectedEntry = ref()

  const notEditingMenuItems = ref([
    {
      label: t('openNewTab'),
      icon: 'InternalLinkIcon',
      command: () => openEntryDetails(selectedEntry.value.id)
    },
    {
      label: t('markRead'),
      icon: 'EyeCheckIcon',
      command: () => entryMetaDataPost(selectedEntry.value),
      disabled: () => selectedEntry.value?.readForCurrentUser
    }
  ])

  async function refresh() {
    const newEntries = (
      await useGeneratedInfoboardEntryStore().findAll({
        query: {
          itemsPerPage: props.items,
          /*'order[updatedAt]': 'desc',*/
          'order[createdAt]': 'desc',
          'type[]': visibleTypes.value,
          unread: visibleState.value
        }
      }, { forceRefresh: true })
    ).value

    if (newEntries.length == 0) return
    employees.value = (await employeeStore.fetchAllByIriAsDictionary(newEntries.map((e) => e.creator as string))).value as Record<string, EmployeeJsonldDefaultRead>
    entries.value = newEntries.filter((e) => !!e)

    const relevantAvatars = await useGeneratedEmployeeAvatarStore().findAll({
      query: {
        'employee[]': Object.values(employees.value!).map((e: EmployeeJsonldDefaultRead) => e.id!)
      }
    })
    avatars.value = keyBy(relevantAvatars, (d: EmployeeAvatarJsonldDefaultRead) => d.employee) as Record<number, EmployeeAvatarJsonldDefaultRead>
  }

  onMounted(async () => {
    isLoading.value = true
    await refresh()
    isLoading.value = false
  })

  const avatars = ref<Record<number, EmployeeAvatarJsonldDefaultRead>>()

  const $formatter = useFormatter()

  const RouterLinkComponent = markRaw(RouterLink)

  const onContextMenu = (event: MouseEvent) => {
    if (isEditing.value) {
      event.preventDefault()
      menu.value?.show(event)
    }
  }

  const visibleTypes = ref(props.visibleTypes)
  const itemsVisible = ref<number>(props.items)
  const visibleState = ref<boolean>(props.visibleState)

  const toggleType = (type: string) => {
    visibleTypes.value.includes(type)
      ? visibleTypes.value = visibleTypes.value.filter(t => t !== type)
      : visibleTypes.value.push(type)
    infoboardWidgetBus.emit({
      visibleTypes: visibleTypes.value,
      items: itemsVisible.value,
      visibleState: visibleState.value
    })
  }

  const onItemsNumChange = (num: number) => {
    itemsVisible.value = num
    infoboardWidgetBus.emit({
      items: itemsVisible.value,
      visibleTypes: visibleTypes.value,
      visibleState: visibleState.value
    })
  }

  const onReadTypeChange = (type: boolean) => {
    visibleState.value = type
    infoboardWidgetBus.emit({
      items: itemsVisible.value,
      visibleTypes: visibleTypes.value,
      visibleState: visibleState.value
    })
  }

  const behaviorHandlers: Record<string, (item: any) => void> = {
    'toggle-type': (item) => toggleType(item.value),
    'change-items': (item) => onItemsNumChange(item.value),
    'toggle-read-state': (item) => onReadTypeChange(item.value)
  }

  function getIcon(entry: InfoboardEntryJsonldDefaultRead): string {
    return iconsList[entry.type ?? 'other']
  }

  const onNotEditingContextMenu = (event: MouseEvent, entry: InfoboardEntryJsonldDefaultRead) => {
    if (!isEditing.value) {
      event.preventDefault()
      selectedEntry.value = entry
      notEditingMenu.value?.show(event)
    }
  }

  async function entryMetaDataPost(entry: InfoboardEntryJsonldDefaultRead) {
    const entryMetaData = {
      employee: titrop.value.user.currentIri,
      entry: entry['@id'],
      readAt: DateTime.now().toISO()
    }
    await useGeneratedInfoboardEntryUserMetaStore().post({ body: entryMetaData })
    const entryResp = await useGeneratedInfoboardEntryStore().fetchByIri(entry['@id']).then(d => d.value)
    const index = entries.value.findIndex(e => e['@id'] === entryResp['@id'])
    if (index > -1) {
      entries.value[index] = entryResp
      if (isDetailsDialogVisible.value)
        selectedEntry.value = entryResp
    }
    toastService.add({ severity: 'success', life: 2500, summary: 'Als gelesen markiert' })
  }

  function openEntryDetails(id: InfoboardEntryJsonldDefaultRead['id']) {
    const route = Router.resolve({
      name: 'infoboard_show_entry',
      params: { id }
    })

    window.open(route.href, '_blank')
  }

  const onEntryClick = (entry: InfoboardEntryJsonldDefaultRead) => {
    selectedEntry.value = entry
    isDetailsDialogVisible.value = true
  }

  watchEffect(() => {
    if (menu.value && !originalHide) {
      originalHide = menu.value.hide
      menu.value.hide = function() {
        if (!preventClose.value) {
          originalHide?.call(this)
        }
        preventClose.value = false
      }
    }
  })

  useEventBus<ProxyContextMenuPayload>('dashboard:infoboard:contextmenu').on((payload) => {
    onContextMenu(payload.event)
  })
</script>

<template>
  <WidgetSkeleton v-if="isLoading" variant="infoboard" />
  <div v-else-if="entries.length > 0"
    class="w-full flex flex-col gap-y-2 gap-x-1 @2xl:grid @2xl:grid-cols-2 px-1 pb-1">

    <component
      is="div"
      v-for="entry in entries"
      :key="entry['@id']"
      :class="{ 'font-semibold': !entry.readForCurrentUser, 'font-normal': entry.readForCurrentUser }"
      :to="{ name: 'infoboard_show_entry', params: { id: entry.id } }"
      @click="onEntryClick(entry)"
      @contextmenu="onNotEditingContextMenu($event, entry)"
      class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-950 rounded-lg border border-gray-300 p-2 flex flex-col shadow-sm hover:border-gray-400 justify-between">
      <div class="flex items-center text-sm">
        <div class="mr-2 shrink-0" v-if="!entry.readForCurrentUser">
          <OverlayBadge>
            <component :is="getIcon(entry)" />
          </OverlayBadge>
        </div>
        <div v-else class="mr-1">
          <component :is="getIcon(entry)" />
        </div>
        <span class="block break-all whitespace-normal">{{ entry.title }}</span>
      </div>

      <div class="flex-0 flex flex-row text-xs text-gray-500 mt-1">
        <div class="flex w-full flex-row justify-between">
          <div class="text-left">
            <span v-if="employees && entry.creator">{{ employees[entry.creator].displayname }}</span>
          </div>
          <div class="text-right">
            <span>{{ $formatter.DateTime(entry.updatedAt ?? entry.createdAt) }}</span>
          </div>
        </div>
        <div>
          <Image
            v-if="employees && entry.creator && avatars && avatars[employees[entry.creator].id!]?.avatars?.widget_profile_avatar"
            :src="avatars[employees[entry.creator].id!].avatars?.widget_profile_avatar"
            class="hidden md:block ml-auto"
            image-class="h-10 rounded-full" />
        </div>
      </div>
    </component>

    <ContextMenu
      v-if="!isEditing"
      ref="notEditingMenu"
      :model="notEditingMenuItems">
      <template #itemicon="itm">
        <component
          :is="itm.item.icon"
          :class="[itm.class]" />
      </template>
    </ContextMenu>

    <Dialog
      v-if="isDetailsDialogVisible"
      v-model:visible="isDetailsDialogVisible"
      modal
      :header="t('details')"
      class="w-full md:max-w-[1100px]">
      <div class="w-full text-sm md:items-center flex flex-col md:justify-between md:flex-row">
        <div>
              <span>
                Autor: {{ selectedEntry.creator ? employees?.[selectedEntry.creator]?.displayname : 'unbekannt' }}
              </span>
        </div>
        <div>
          <span>
                Datum: {{ $formatter.DateTime(selectedEntry.createdAt) }}
              </span>
        </div>
      </div>

      <PanelTitled
        :title="selectedEntry.title"
        class="w-full flex flex-col h-auto">
        <p class="text-gray-700 ql-editor" v-titrop-html="selectedEntry.text" />
      </PanelTitled>

      <div class="mt-auto pt-2">
        <div class=" pt-2 flex justify-end items-center">
          <div class="flex">
            <ButtonDefault
              :class="{'p-1 size-9': !isLargeScreen}"
              v-if="!selectedEntry.readForCurrentUser"
              size="small"
              :icon="'EyeCheckIcon'"
              :label="isLargeScreen ? t('read') : undefined"
              outlined
              v-tooltip.top="t('markRead')"
              @click="entryMetaDataPost(selectedEntry)" />
            <OverlayBadge :value="String(selectedEntry?.responses?.length ?? 0)" size="small">
              <ButtonDefault
                class="ml-2"
                :class="{'p-1 size-9': !isLargeScreen}"
                :label="isLargeScreen ? t('comments') : undefined"
                size="small"
                outlined
                icon="CommentIcon"
                @click="openEntryDetails(selectedEntry.id)" />
            </OverlayBadge>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
  <div v-else>
    <Message severity="info" size="small">
      <template #icon>
        <component is="InfoIcon" />
      </template>
      <p>{{ t('noEntries') }}</p>
    </Message>
  </div>

  <ContextMenu
    ref="menu"
    :model="items"
    v-if="isEditing"
    :pt="{
        itemContent: ({ context }) => {
            const item = context.item
            return {
              class: 'flex items-center cursor-pointer' + (
                visibleTypes.includes(item.value)
                || itemsVisible === item.value
                || visibleState === item.value
                ? ' bg-slate-100' : '')
            }
          },
        item: ({context}) => {
            const item = context.item
            const handler = behaviorHandlers[item.behavior]
            if (!item.selectable) {
              if(!handler) return {}

              return {
              onpointerdown: (e: PointerEvent) => {
                handler(item)
                }
              }
            }

            return {
              onpointerdown: (e: PointerEvent) => {
                preventClose = true
                handler(item)
              }
            }
          }
        }">
    <template #itemicon="itm">
      <component
        :is="itm.item.icon"
        :class="[itm.class]" />
    </template>
  </ContextMenu>
</template>

<style scoped>
  :deep(.ql-editor) {
    padding: 0 !important;
  }
</style>

<i18n>
{
  "de": {
    "displayedTypes": "Angezeigte Eintragstypen",
    "information": "Information",
    "question": "Frage",
    "announcement": "Ankündigung",
    "error": "Fehler",
    "other": "Sonstiges",
    "showAll": "Alle anzeigen",
    "unreadOnly": "Nur ungelesene",
    "itemCount": "Anzahl der Einträge",
    "entries": "{count} Einträge",
    "all": "Alle",
    "openNewTab": "In neuem Tab öffnen",
    "markRead": "Als gelesen markieren",
    "read": "Gelesen",
    "comments": "Kommentare",
    "details": "Infoboard-Beitrag Details",
    "noEntries": "Mit den angegebenen Einstellungen wurden keine Einträge gefunden"
  },
  "en": {
    "displayedTypes": "Displayed entry types",
    "information": "Information",
    "question": "Question",
    "announcement": "Announcement",
    "error": "Error",
    "other": "Other",
    "showAll": "Show all",
    "unreadOnly": "Unread only",
    "itemCount": "Number of entries",
    "entries": "{count} entries",
    "all": "All",
    "openNewTab": "Open in new tab",
    "markRead": "Mark as read",
    "read": "Read",
    "comments": "Comments",
    "details": "Infoboard post details",
    "noEntries": "No entries were found with the selected settings"
  }
}
</i18n>
